import { isRef, toRaw } from "vue";
import type { PiniaPluginContext } from "pinia";
import { useUserStore } from "@/stores/user";

/**
 * Stores that are not reset with the others:
 * - auth: purgeAuth() handles it
 * - user: its initial state is read from localStorage, so it may already hold
 *   the previous user; it is cleared with delete_cached_user() instead
 * - config, theme, body: layout and UI state, not user data
 */
const SKIPPED_STORES = new Set(["auth", "user", "config", "theme", "body"]);

const resetters = new Map<string, () => void>();

const clone = <T>(value: T): T => {
  try {
    return structuredClone(value);
  } catch {
    return value;
  }
};

/**
 * Pinia plugin that remembers each store's initial state, so resetAllStores()
 * can restore it. Setup stores have no built-in $reset().
 */
export function storeResetPlugin({ store }: PiniaPluginContext) {
  if (SKIPPED_STORES.has(store.$id)) return;

  const rawState = toRaw(store.$state) as Record<string, any>;
  const unwrap = (key: string) => {
    const raw = rawState[key];
    return toRaw(isRef(raw) ? raw.value : raw);
  };
  const initial: Record<string, any> = {};
  Object.keys(rawState).forEach(key => { initial[key] = clone(unwrap(key)); });

  resetters.set(store.$id, () => {
    const state = store.$state as Record<string, any>;
    Object.keys(initial).forEach(key => {
      const fresh = clone(initial[key]);
      const current = state[key];
      // A store's functions hold on to its reactive arrays and objects, so
      // those are reset in place; refs and primitives are reassigned.
      if (isRef(rawState[key])) {
        state[key] = fresh;
      } else if (Array.isArray(current)) {
        current.splice(0, current.length, ...fresh);
      } else if (current && typeof current === "object" && fresh && typeof fresh === "object") {
        Object.keys(current).forEach(k => { if (!(k in fresh)) delete current[k]; });
        Object.assign(current, fresh);
      } else {
        state[key] = fresh;
      }
    });
  });
}

/**
 * Clear every store that holds the logged in user's data, so the next user
 * does not see it.
 */
export function resetAllStores(): void {
  resetters.forEach(reset => reset());
  useUserStore().delete_cached_user();
}
